import { Link as RouterLink } from 'react-router'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

// DS `FirstlyMenuLayout` の header は collapsed / expanded のどちらでも
// `paddingStart="15px"` / `paddingEnd="1"` (= 4px) を掛ける。
//   - expanded: design の `.sidebar-header` も左 15px なので DS の padding をそのまま使う
//     (ここで更に padding を足すと 27px になり、 ロゴの左余白が広くなる)
//   - collapsed: design は `.sidebar.collapsed .sidebar-header { padding: 0 }` + 中央寄せ
//     で rail 48px のど真ん中に置く。 DS の padding が残ると左 15px / 右 4px の非対称に
//     なるため、 負 margin で打ち消して 48px 幅を取り戻してから中央に置く
const DS_HEADER_PADDING_START_PX = 15
const DS_HEADER_PADDING_END_PX = 4

// eBalance ブランドアセット (2026-07-10 差し替え)。 path は design の
// `design/js/include-nav.js` の LOGO_SVG / LOGO_ICON_SVG と同一。
//   - fg     … 文字 + マーク左半分 (藍)
//   - accent … マーク右半分 (コーラル)
// 支給 SVG は `<defs><style>` に cls-1/2/3 という汎用クラス名を持ち、 inline SVG だと
// ページ全体へ漏れるため、 塗りは fill 属性で直接指定する (design 側も同じ理由で class 化)。
// 色はブランド規定色だが eclair に同値の token があるのでそちらを参照する。
const LOGO_FG = LightModeSemanticColors.Brand.blue.value // #0c00c5
const LOGO_ACCENT = LightModeSemanticColors.Brand.pink.value // #ff6e6e

// 支給 artboard (149.48 角) は余白を含むため、 マークの見え幅を design と揃えるには
// 実寸を上げる必要がある (design `.brand-logo-icon` = 28px)。
const LogoIcon = (): React.ReactElement => (
  <svg
    aria-label="eBalance"
    role="img"
    style={{ width: '28px', height: '28px', flexShrink: 0 }}
    viewBox="0 0 149.48 149.48"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M88.4,75.88l-12.31,7.11,41.68,24.06c.93.54,2,.83,3.08.83s2.14-.29,3.08-.83c1.9-1.1,3.08-3.14,3.08-5.33v-54.62c0-1.29-.33-2.57-1.06-3.63-1.93-2.79-5.51-3.41-8.17-1.87l-37.3,21.54,12.31,7.11,21.91-12.65v33.46"
      fill={LOGO_ACCENT}
    />
    <path
      d="M31.72,43.15c-1.87-1.08-4.29-1.08-6.16,0-1.9,1.1-3.08,3.14-3.08,5.33v54.78c0,2.19,1.18,4.24,3.08,5.33.95.55,2.01.82,3.08.82s2.13-.27,3.08-.82l37.3-21.54-12.31-7.11-21.91,12.65v-33.46l41.29,23.84h0s12.31-7.11,12.31-7.11L31.72,43.15Z"
      fill={LOGO_FG}
    />
  </svg>
)

// design `.brand-logo` = height 26px / width auto。 新ロゴは文字高が viewBox の 47% しか
// 占めないため、 旧ロゴ (18px) のままだと文字が一回り小さく見える。
const LogoFull = (): React.ReactElement => (
  <svg
    aria-label="eBalance"
    role="img"
    style={{ height: '26px', width: 'auto', flexShrink: 0 }}
    viewBox="0 0 590.41 132.64"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M224.03,35.66h27.29c9.74,0,17.53,7.79,17.53,17.04,0,3.89-1.66,6.82-3.21,8.76-.88,1.17-1.95,2.14-3.12,2.92,1.66.88,3.12,2.04,4.38,3.51,2.24,2.43,4.38,6.13,4.38,11.59,0,9.25-8.28,17.53-19.47,17.53h-27.78v-61.34ZM251.32,60.98c4.97,0,8.76-3.89,8.76-8.28s-3.8-8.28-8.76-8.28h-18.04v16.55h18.04ZM251.81,88.24c5.84,0,10.22-4.38,10.22-9.74s-4.38-9.74-10.22-9.74h-18.53v19.47h18.53Z"
      fill={LOGO_FG}
    />
    <path
      d="M486.93,59.32c6.21,0,10.16,3.56,11.85,8.66h10.72c-2.26-9.68-10.72-17.31-22.58-17.31-13.55,0-24.27,9.67-24.27,23.42,0,13.75,10.72,23.42,24.27,23.42,11.85,0,20.32-7.64,22.58-17.31h-10.72c-1.69,5.09-5.64,8.66-11.85,8.66-7.34,0-14.25-6.11-14.25-14.77,0-8.66,6.91-14.77,14.25-14.77Z"
      fill={LOGO_FG}
    />
    <path
      d="M434.26,50.68c-12.27,0-20.71,4.88-20.71,4.88v41.45s9.17,0,9.17,0v-35.1s5.26-2.17,10.86-2.17c7.41,0,11.31,4.53,11.31,11v26.28s9.17,0,9.17,0v-26.46c0-12.22-7.86-19.86-19.79-19.86Z"
      fill={LOGO_FG}
    />
    <path
      d="M189.02,50.66c-12.83,0-23.42,9.67-23.42,23.42,0,13.75,10.6,23.42,23.98,23.42,11.71,0,18.4-6.11,21.75-13.75h-10.6c-1.67,2.04-5.02,5.09-11.15,5.09-7.25,0-13.1-5.09-13.66-10.69h35.97s.56-2.04.56-4.07c0-13.75-10.6-23.42-23.42-23.42ZM175.93,70.52c1.12-6.62,6.41-11.2,13.1-11.2,7.81,0,12.83,4.58,13.94,11.2h-27.04Z"
      fill={LOGO_FG}
    />
    <path
      d="M301.8,50.66c-13.07,0-23.42,9.67-23.42,23.42,0,13.75,10.35,23.42,23.42,23.42,5.88,0,11.2-1.97,15.28-5.38l1.06,4.87h7.08s0-9.14,0-22.92c0-13.78-9.78-23.42-23.42-23.42ZM301.8,88.85c-7.08,0-13.75-6.11-13.75-14.77,0-8.66,6.67-14.77,13.75-14.77,7.08,0,13.62,6.11,13.62,14.77,0,8.66-6.54,14.77-13.62,14.77Z"
      fill={LOGO_FG}
    />
    <path
      d="M379.51,50.66c-13.07,0-23.42,9.67-23.42,23.42,0,13.75,10.35,23.42,23.42,23.42,5.88,0,11.2-1.97,15.28-5.38l1.06,4.87h7.08s0-9.14,0-22.92c0-13.78-9.78-23.42-23.42-23.42ZM379.51,88.85c-7.08,0-13.75-6.11-13.75-14.77,0-8.66,6.67-14.77,13.75-14.77,7.08,0,13.62,6.11,13.62,14.77,0,8.66-6.54,14.77-13.62,14.77Z"
      fill={LOGO_FG}
    />
    <path
      d="M539.49,50.66c-12.83,0-23.42,9.67-23.42,23.42,0,13.75,10.6,23.42,23.98,23.42,11.71,0,18.4-6.11,21.75-13.75h-10.6c-1.67,2.04-5.02,5.09-11.15,5.09-7.25,0-13.1-5.09-13.66-10.69h35.97s.56-2.04.56-4.07c0-13.75-10.6-23.42-23.42-23.42ZM526.39,70.52c1.12-6.62,6.41-11.2,13.1-11.2,7.81,0,12.83,4.58,13.94,11.2h-27.04Z"
      fill={LOGO_FG}
    />
    {/* design 側は `<rect x=337.69 y=35.64 width=9.17 height=61.36>` (= "l" の縦棒)。
        raw SVG の width / height を chakra-ui/props-order が style props と誤認し、
        jsx の alphabetical sort と衝突して解けなくなるため、 等価な path で書く。 */}
    <path d="M337.69,35.64h9.17v61.36h-9.17Z" fill={LOGO_FG} />
    <path
      d="M93.41,66.32l-12.31,7.11,41.68,24.06c.93.54,2,.83,3.08.83s2.14-.29,3.08-.83c1.9-1.1,3.08-3.14,3.08-5.33v-54.62c0-1.29-.33-2.57-1.06-3.63-1.93-2.79-5.51-3.41-8.17-1.87l-37.3,21.54,12.31,7.11,21.91-12.65v33.46"
      fill={LOGO_ACCENT}
    />
    <path
      d="M36.73,33.6c-1.87-1.08-4.29-1.08-6.16,0-1.9,1.1-3.08,3.14-3.08,5.33v54.78c0,2.19,1.18,4.24,3.08,5.33.95.55,2.01.82,3.08.82s2.13-.27,3.08-.82l37.3-21.54-12.31-7.11-21.91,12.65v-33.46l41.29,23.84h0s12.31-7.11,12.31-7.11l-56.68-32.72Z"
      fill={LOGO_FG}
    />
  </svg>
)

export const Logo = ({
  collapsed,
}: {
  collapsed: boolean
}): React.ReactElement => {
  return (
    <RouterLink
      style={{
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        height: '100%',
        justifyContent: collapsed ? 'center' : 'flex-start',
        textDecoration: 'none',
        userSelect: 'none',
        width: '100%',
        ...(collapsed
          ? {
              marginInlineEnd: `-${DS_HEADER_PADDING_END_PX}px`,
              marginInlineStart: `-${DS_HEADER_PADDING_START_PX}px`,
              width: `calc(100% + ${
                DS_HEADER_PADDING_START_PX + DS_HEADER_PADDING_END_PX
              }px)`,
            }
          : {}),
      }}
      to="/"
    >
      {collapsed ? <LogoIcon /> : <LogoFull />}
    </RouterLink>
  )
}
